/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { Button, Typography2, breakpoints } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import Image from 'bundles/ent-website/contentful/core/Image';
import type { ResourceHubCardItem } from 'bundles/ent-website/types';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/ent-website';

const styles = {
  resourceItem: css`
    cursor: pointer;
    background: var(--cds-color-white-0);
    border-radius: var(--cds-spacing-100);
    transition: all 0.15s;
    /* stylelint-disable-next-line color-function-notation */
    box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 10%);

    :hover,
    :link,
    :active,
    :visited {
      text-decoration: none;
    }

    :hover {
      button {
        text-decoration: underline;
      }
    }

    @media (hover: hover) and (pointer: fine) {
      :hover {
        /* stylelint-disable-next-line color-function-notation */
        box-shadow: rgba(99, 99, 99, 20%) 0 2px 8px 0;
      }
    }

    img {
      border-top-right-radius: var(--cds-spacing-100);
      border-top-left-radius: var(--cds-spacing-100);
    }
    ${breakpoints.down('xs')} {
      img {
        width: 100%;
      }
    }
  `,
  resourceItemDetails: css`
    padding: var(--cds-spacing-200);
    height: 200px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    h3 {
      flex: 1;
    }

    button {
      padding: 0;
    }
  `,
  // 16:9 aspect ratio
  cardImageCnt: css`
    position: relative;
    aspect-ratio: 16 / 9;
  `,
  cardImage: css`
    width: 100%;
    height: 100%;
    object-fit: cover;
  `,
  eyebrow: css`
    color: var(--cds-color-neutral-primary-weak);
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
    min-height: 1rem;
  `,
  headline: css`
    padding-top: 8px;
    font-size: 22px;
  `,
};

export type ResourceHubCardProps = {
  item: ResourceHubCardItem;
};

export default function ResourceHubCard({ item }: ResourceHubCardProps) {
  return (
    <TrackedLink2
      data-testid="ent_resource_hub_card"
      trackingName="resource_hub_card"
      data={{ slug: item?.slug }}
      css={styles.resourceItem}
      key={item?.slug}
      href={item?.cta?.ctaTarget ?? '#'}
      target={item?.cta?.shouldOpenInNewWindow ? '_blank' : undefined}
      rel="noreferrer"
    >
      {item?.image && (
        <div css={styles.cardImageCnt}>
          <Image content={item.image} css={styles.cardImage} />
        </div>
      )}
      <div css={styles.resourceItemDetails}>
        <Typography2 component="p" css={styles.eyebrow}>
          {item?.eyebrow ?? _t('Resource')}
        </Typography2>

        <Typography2 component="h3" variant="subtitleMedium" css={styles.headline}>
          {item?.headline}
        </Typography2>
        {item?.cta?.ctaText && item?.showButton !== false && (
          <Button
            size="medium"
            variant="ghost"
            css={css`
              background-color: transparent !important;
            `}
            icon={<ArrowNextIcon />}
          >
            {item?.cta?.ctaText}
          </Button>
        )}
      </div>
    </TrackedLink2>
  );
}
